import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import SideBarListItemIcon from "./SideBarListItemIcon";
import SideBarListItemText from "./SideBarListItemText";
import SideBarDrawer from "./SideBarDrawer";
import AppBar from "./AppBar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./../../app/store";
import { generateNavListWithIcons } from "../../utils/navigationConfig";

import Logo from "./../../images/logo.png";
import Profile from "./Profile";

interface NavItem {
  type: "nav";
  nav: string;
  path: string;
  icon: React.ReactNode;
}

interface DividerItem {
  type: "divider";
}

const drawerWidth = 240;
export const NAV_LIST: (NavItem | DividerItem)[] = generateNavListWithIcons();

const DrawerHeader = styled("div")(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  };
});

type MyComponentProps = {
  children: React.ReactNode;
};

export default function MiniDrawer({ children }: MyComponentProps) {
  const allowedPages = useSelector(
    (state: RootState) => state.auth.allowedPages
  );
  const userRole = useSelector((state: RootState) => state.auth.userRole);
  const [filteredNav, setFilteredNav] = React.useState<
    (NavItem | DividerItem)[]
  >([]);

  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const onClick = (path: string) => {
    navigate(path);
  };

  React.useEffect(() => {
    let tempFilteredNav: (NavItem | DividerItem)[] = [],
      navElPresent = false;

    NAV_LIST.forEach((el) => {
      if (el.type === "nav") {
        if (userRole === "admin" || allowedPages.includes(el.nav)) {
          tempFilteredNav.push(el);
          navElPresent = true;
        }
      } else if (el.type === "divider") {
        if (navElPresent) {
          tempFilteredNav.push(el);
          navElPresent = false;
        }
      }
    });
    setFilteredNav(tempFilteredNav);
  }, [allowedPages]);

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} drawerWidth={drawerWidth}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[open && { display: "none" }]}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component={"img"}
            src={Logo}
            alt={"Logo"}
            sx={{ width: "auto", height: 30, mr: "auto" }}
          />
          <Profile />
        </Toolbar>
      </AppBar>
      <SideBarDrawer variant="permanent" open={open} drawerWidth={drawerWidth}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {filteredNav.map((navData) => {
            if (navData.type === "nav") {
              return (
                <ListItem
                  key={navData.nav}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    sx={[
                      {
                        minHeight: 36,
                        px: 2,
                        color: theme.palette.primary.main,
                        transition: "all 350ms ease-in-out",
                        "&:hover": {
                          color: theme.palette.secondary.light,
                        },
                      },
                      open
                        ? {
                            justifyContent: "initial",
                          }
                        : {
                            justifyContent: "center",
                          },
                    ]}
                    onClick={onClick.bind(null, navData.path)}
                  >
                    <SideBarListItemIcon open={open}>
                      {navData.icon}
                    </SideBarListItemIcon>
                    <SideBarListItemText primary={navData.nav} open={open} />
                  </ListItemButton>
                </ListItem>
              );
            }

            if (navData.type === "divider") {
              return <Divider />;
            }

            return <></>;
          })}
        </List>
        {/* <Divider /> */}
      </SideBarDrawer>
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 1.5 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
